import React from 'react';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import classNames from 'classnames';
import { DragDropContext } from 'react-beautiful-dnd';

import DataTableBody from './DataTableBody';
import DataTableHeader from './DataTableHeader';
import DataTableFooter from './DataTableFooter';
import utils from './utils';

import './data-table.scss';

const defaultProps = {
    className: '',
    data: [],
    isLoading: false,
    isDraggable: false,
    resetPageOnSort: true,
    onFetchData: () => { },
    onRowClick: () => { },
    onDragEnd: () => { },
    pageable: true,
    page: {
        count: null,
        number: 0,
        size: 20,
    },
    sortable: true,
    sort: [
        {
            selector: '',
            direction: 'ASC',
        },
    ],
    disabledDragRows: [],
    handlePageChange: () => { },
    resetParentData: () => { },
    stickyHeader: false,
    disableDefaultSorting: false,
};

class DataTable extends React.Component {
    static getDerivedStateFromProps(nextProps, previousState) {
        const state = {};

        if (nextProps.pageable) {
            state.page = defaultsDeep({}, previousState.page, nextProps.page);
        }

        if (nextProps.sortable) {
            let defaultSort = {};

            if (previousState.sort && previousState.sort.length && previousState.sort[0].selector) {
                defaultSort = previousState.sort;
            } else if (nextProps.sort && nextProps.sort.length && nextProps.sort[0].selector) {
                defaultSort = nextProps.sort;
            } else if (!nextProps.disableDefaultSorting) {
                const sortedColumn = nextProps.columns.find(col =>
                    col.sort && !!col.sort.default);
                const accessor = sortedColumn ?
                    sortedColumn.accessor
                    :
                    nextProps.columns[0].accessor;
                defaultSort = [{ selector: utils.normalizeAccessor(accessor) }];
            } else {
                defaultSort = [];
            }

            state.sort = defaultsDeep(defaultSort, defaultProps.sort);
        }

        return state;
    }

    constructor(props) {
        super(props);

        this.state = {
            page: {
            },
            sort: [],
        };

        this.fetchData = this.fetchData.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onNumberOfItemsChange = this.onNumberOfItemsChange.bind(this);
        this.onSortChange = this.onSortChange.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.renderTable = this.renderTable.bind(this);
    }

    componentDidMount() {
        this.fetchData();
    }

    onPageChange(pageNumber) {
        this.props.handlePageChange();
        this.updateParams({
            page: {
                number: pageNumber - 1,
            },
        }, this.fetchData);
    }

    /**
     * Update state.page.count value to the number passed as parameter
     * @param {*} sizeValue
     */
    onNumberOfItemsChange(sizeValue) {
        this.props.resetParentData();
        this.updateParams({
            page: {
                number: 0,
                size: sizeValue,
                itemsPerPage: sizeValue,
            },
        }, this.fetchData);
    }

    onSortChange(sort) {
        let page;
        if (this.props.resetPageOnSort) {
            page = {
                number: 0,
            };
        }
        this.updateParams({
            page,
            sort,
        }, this.fetchData);
    }

    onDragEnd(result) {
        const { onDragEnd } = this.props;
        const { destination, source } = result;

        if (!destination || destination.index === source.index) {
            return;
        }

        if (destination.index === source.index) {
            return;
        }

        if (onDragEnd && typeof onDragEnd === 'function') {
            onDragEnd(source.index, destination.index);
        }
    }

    getParams() {
        const { pageable, sortable } = this.props;
        const { page, sort } = this.state;
        const params = {};

        if (pageable) {
            params.page = page;
        }

        if (sortable) {
            params.sort = sort;
        }

        return params;
    }

    updateParams(params = {}, callback) {
        const state = defaultsDeep(params, this.state);
        this.setState(state, () => callback());
    }

    fetchData() {
        const { onFetchData } = this.props;
        if (onFetchData && typeof onFetchData === 'function') {
            onFetchData(this.getParams());
        }
    }

    renderTable() {
        const { className, stickyHeader } = this.props;
        const { sort } = this.state;
        const tableClassName = classNames('data-table', className);
        const containerClassName = classNames(tableClassName, 'container');

        return (
            <div className={containerClassName}>
                <table className={tableClassName}>
                    <DataTableHeader
                        {...this.props}
                        onSortChange={this.onSortChange}
                        sortedBy={sort}
                        stickyHeader={stickyHeader}
                    />
                    <DataTableBody
                        {...this.props}
                    />
                    <DataTableFooter
                        {...this.props}
                        onPageChange={this.onPageChange}
                        onNumberOfItemsChange={this.onNumberOfItemsChange}
                    />
                </table>
            </div >
        );
    }

    render() {
        const { isDraggable } = this.props;

        return (
            isDraggable ?
                <DragDropContext onDragEnd={this.onDragEnd}>
                    {this.renderTable()}
                </DragDropContext>
                :
                <React.Fragment>
                    {this.renderTable()}
                </React.Fragment>
        );
    }
}

DataTable.propTypes = {
    className: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({
        accessor: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.string,
            PropTypes.bool,
        ]).isRequired,
        align: PropTypes.oneOf([
            'center',
            'left',
            'right',
        ]),
        className: PropTypes.string,
        Cell: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.func,
            PropTypes.string,
            PropTypes.bool,
        ]),
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool,
        ]).isRequired,
    })).isRequired,
    data: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    isDraggable: PropTypes.bool,
    resetPageOnSort: PropTypes.bool,
    onFetchData: PropTypes.func,
    onRowClick: PropTypes.func,
    onDragEnd: PropTypes.func,
    resetParentData: PropTypes.func,
    pageable: PropTypes.bool,
    page: PropTypes.shape({
        count: PropTypes.number,
        number: PropTypes.number,
        size: PropTypes.number,
    }),
    sortable: PropTypes.bool,
    sort: PropTypes.arrayOf(PropTypes.shape({
        selector: PropTypes.string,
        direction: PropTypes.oneOf([
            'ASC',
            'DESC',
        ]),
    })),
    disabledDragRows: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ])),
    handlePageChange: PropTypes.func,
    stickyHeader: PropTypes.bool,
    disableDefaultSorting: PropTypes.bool,
};

DataTable.defaultProps = defaultProps;

export default DataTable;
